<template>
  <div class="content-list-item">

    <!-- Header -->
    <div class="content-list-item-header">
      <div class="content-list-item-header-icon">
        <img v-if="item.icon" :src="'media/' + item.icon" :alt="item.title" class="content-list-item-header-icon-image" width="24" height="24" loading="lazy" fetchpriority="auto">
      </div>
      <h1 class="content-list-item-header-title"> {{ item.title }} </h1>
    </div>
    <!---->

    <!-- Content -->
    <div class="content-list-item-block">
      <div class="content-list-item-block-content isActive">

        <!-- Description -->
        <div class="content-list-item-block-content-description"
             ref="description"
             v-html="item.description"
        ></div>
        <!---->

        <button class="content-list-item-block-content-btn" :class="{ isFixed: !isFormShowed }" @click="toggleForm"> {{ $t('Send an application') }} </button>

        <!-- Form -->
        <div class="content-list-item-block-content-form"
             :class="{ isActive: isFormShowed }"
             ref="form"
        >

          <!-- Fullname field -->
          <base-input class="base-input-small content-list-item-block-content-form-field"
                      type="text"
                      name="name"
                      id="full_name"
                      :placeholder="`${$t('Fullname')} *`"
                      :maxlength="50"
                      :validations="[
                        {
                          condition: !$v.fullname.required && $v.fullname.$error,
                          text: $t('Field is required')
                        },
                        {
                          condition: !$v.fullname.minLength,
                          text: $t('Name must have at least 2 letters.')
                        }
                      ]"
                      v-model.trim="fullname"
          />
          <!---->

          <!-- Email address field -->
          <base-input class="base-input-small content-list-item-block-content-form-field"
                      type="email"
                      name="email"
                      id="email"
                      :placeholder="`${$t('Email address')} *`"
                      :validations="[
                        {
                          condition: !$v.email.required && $v.email.$error,
                          text: $t('Field is required')
                        },
                        {
                          condition: !$v.email.email,
                          text: $t('Please provide valid e-mail address.')
                        }
                      ]"
                      v-model.trim="email"
          />
          <!---->

          <!-- Phone number field -->
          <base-input class="base-input-small content-list-item-block-content-form-field"
                      type="text"
                      :placeholder="$t('Phone Number *')"
                      name="phone-number"
                      id="phone_number"
                      :mask="{
                        mask: '+38(999)-999-9999',
                        showMaskOnHover: false,
                        autoUnmask: true
                      }"
                      :validations="[
                        {
                          condition: $v.phone.$error && !$v.phone.required,
                          text: $t('Field is required.')
                        },
                        {
                          condition: $v.phone.$error && !$v.phone.minLength,
                          text: $t('Incorrect type of number')
                        },
                        {
                          condition: $v.phone.$error && !$v.phone.incorrectOperatorCodeWithTwo0,
                          text: $t('Operator code cannot start with 00')
                        },
                        {
                          condition: $v.phone.$error && !$v.phone.incorrectOperatorCode && $v.phone.required,
                          text: $t('Operator code must start with 0')
                        }
                      ]"
                      v-model.trim="phone"
          />
          <!---->

          <!-- Attach file -->
          <div class="content-list-item-block-content-form-file">
            <a class="content-list-item-block-content-form-file-title no-underline"
               @click="$refs.fileInput.click()"
            > {{ $t('Attach resume') }} </a>
            <input type="file"
                   hidden
                   class="content-list-item-block-content-form-file-field"
                   ref="fileInput"
                   accept=".doc, .docs, .pdf, .png"
                   @change="uploadFile"
            >
            <span class="content-list-item-block-content-form-file-text"> {{ fileName }} </span>
          </div>
          <!---->

          <button-full class="content-list-item-block-content-form-btn"
                       :disabled="$v.$invalid || file === null"
                       @click.native="addResume"
          > {{ $t('Send data') }} </button-full>
        </div>
        <!---->

      </div>
    </div>
    <!---->

  </div>
</template>

<script>
import { required, minLength, email, sameAs } from 'vuelidate/lib/validators'
import BaseInput from 'theme/components/core/blocks/Form/BaseInput'
import ButtonFull from 'theme/components/theme/ButtonFull'

// Phone number validation methods
const incorrectOperatorCode = (value, vm) => (value.charAt(0) === '0');
const incorrectOperatorCodeWithTwo0 = (value, vm) => (value.charAt(1) !== '0');

export default {
  components: {
    BaseInput,
    ButtonFull
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    clearVacation: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      // Styles vars
      isDescriptionShowed: false,
      isFormShowed: false,

      // Form vars
      fullname: '',
      email: '',
      phone: '',
      file: null,
      fileName: '',
    }
  },
  computed: {
    buttonText () {
      return this.isDescriptionShowed ? 'Згорнути опис' : 'Розгорнути опис'
    }
  },
  methods: {
    // Show & hide description container
    toggleDescription () {
      if (this.isDescriptionShowed) {
        this.isDescriptionShowed = false
        this.isFormShowed = false
        this.clearAllFormFields()
        this.scrollTo('description')
      } else {
        this.isDescriptionShowed = true
      }
    },

    // Show & hide form container
    toggleForm () {
      if (this.isFormShowed) {
        this.isFormShowed = false
        this.clearAllFormFields()
      } else {
        this.isFormShowed = true
        setTimeout(() => this.scrollTo('form'), 210)
      }
    },

    // Clear all fields
    clearAllFormFields () {
      this.fullname = ''
      this.email = ''
      this.phone = ''
      this.file = null
      this.fileName = ''
      this.$v.$reset();
    },

    // Scroll to 'ref'
    scrollTo (ref) {
      const el = this.$refs[ref]
      if (el) {
        el.scrollIntoView({behavior: 'smooth'})
      }
    },

    // Upload file in vacancy resume
    uploadFile (event) {
      const applyFile = event.target.files[0]
      if (applyFile !== undefined) {
        this.file = event.target.files[0]
        this.fileName = applyFile.name
      }
    },

    // Add new resume
    addResume () {
      let payload = {
        vacancy_data: {
          title: this.item.title,
          location: this.item.location_id
        },
        user_personal_data: {
          name: this.fullname,
          email: this.email,
          telephone: this.phone,
        },
        itemId: this.item.id
      }

      this.$emit('show-vacancy-success-modal', payload)
    }
  },
  watch: {
    'clearVacation.clear': {
      handler () {
        if (this.clearVacation.clear && this.clearVacation.vacancyId === this.item.id) {
          this.clearAllFormFields();
        }
      }
    }
  },
  validations: {
    fullname: {
      required,
      minLength: minLength(2)
    },
    email: {
      required,
      email
    },
    phone: {
      required,
      minLength: minLength(10),
      incorrectOperatorCode,
      incorrectOperatorCodeWithTwo0
    }
  }
}
</script>

<style lang="scss" scoped>
  .content-list-item {
    width: calc(100% / 2 - 10px);
    margin-right: 20px;
    margin-bottom: 20px;
    position: relative;

    @media only screen and (max-width: 768px) {
      width: 100%;
      margin-right: 0;
    }

    &:nth-child(even) {
      margin-right: 0;
    }

    /* Header */
    &-header {
      display: flex;
      align-items: flex-end;

      &-icon {
        width: 30px;
        height: 30px;

        display: flex;
        justify-content: center;
        align-items: center;

        background-color: #f5f5f5;
        border-top-left-radius: 25px;
        border-top-right-radius: 25px;

        &-image {
          width: 20px;
          height: 20px;
        }
      }

      &-title {
        margin: 0;
        padding: 8px 10px;

        color: #333;
        font-size: 16px;
        font-weight: 600;
      }
    }
    /* --- */

    /* Main container */
    &-block {
      padding: 20px;

      border-top-right-radius: 5px;
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
      background-color: #f5f5f5;

      &-btn {
        padding: 7px 22px 7px 40px;
        position: relative;

        color: #fff;
        font-size: 14px;
        font-weight: 600;

        background-color: #11bb2e;
        border-radius: 25px;
        border: none;
        outline: none;

        transition: .2s;

        &:hover {
          cursor: pointer;
          background-color: rgba(17, 187, 46, .8);
        }

        &.isActive:before {
          transform: rotate(180deg);
        }

        &:before {
          width: 30px;
          height: 30px;
          margin: auto;

          position: absolute;
          top: 0;
          left: -2px;
          bottom: 0;
          content: '';

          background-image: url('../../../../assets/static/vacancies/arrow.png');
          background-position: center;
          background-size: 16px;
          background-repeat: no-repeat;

          background-color: #fff;
          border-radius: 50%;

          transition: .2s;
        }
      }

      /* Content */
      &-content {
        padding-right: 20px;

        margin-bottom: 20px;
        transition: .2s ease-out;

        &.isActive {
          max-height: 400px;
          margin-top: 20px;

          overflow-x: hidden;
          overflow-y: scroll;
          opacity: 1;

          transition: .2s ease-in;
        }

        /* Scroll styles */
        &::-webkit-scrollbar {
          width: 8px;
        }

        &::-webkit-scrollbar-track {
          background: #e6e6e6;
        }

        &::-webkit-scrollbar-thumb {
          background: #11bb2e;
          border-radius: 5px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: rgba(#11bb2e, .5);
        }
        /* --- */

        &-description {
          margin-bottom: 20px;
          transition: .3s;

          ::v-deep p {
            margin: 0;
          }

          ::v-deep ul {
            margin: 0;
            padding: 0;
            list-style: none;

            li {
              margin-top: 6px;
              padding-left: 35px;

              position: relative;

              &:before {
                width: 23px;
                height: 23px;
                margin: auto;

                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                content: '';

                background-image: url('../../../../assets/static/vacancies/correct.png');
                background-position: center;
                background-size: 10px 8px;
                background-repeat: no-repeat;

                background-color: #11bb2e;
                border-radius: 50%;
              }
            }
          }
        }

        &-btn {
          padding: 7px 22px 7px 40px;
          position: relative;

          color: #fff;
          font-size: 14px;
          font-weight: 600;

          background-color: #11bb2e;
          border-radius: 25px;
          border: none;
          outline: none;

          transition: .2s;
          &.isFixed {
            position: absolute;
            bottom: 10px;
          }
          &:hover {
            cursor: pointer;
            background-color: rgba(17, 187, 46, .8);
          }

          &:before {
            width: 30px;
            height: 30px;
            margin: auto;

            position: absolute;
            top: 0;
            left: -2px;
            bottom: 0;
            content: '';

            background-image: url('../../../../assets/static/vacancies/book.png');
            background-position: center;
            background-size: 14px 17px;
            background-repeat: no-repeat;

            background-color: #fff;
            border-radius: 50%;
          }
        }

        &-form {
          max-height: 0;
          overflow: hidden;
          opacity: 0;

          display: flex;
          flex-direction: column;
          align-items: flex-start;

          transition: .2s ease-out;

          &.isActive {
            max-height: 300px;
            margin-top: -35px;
            padding-top: 60px;

            opacity: 1;

            transition: .2s ease-in;

            @media only screen and (max-width: 576px) {
              max-height: 360px;
            }
          }

          &-field {
            width: 100%;
            margin-bottom: 10px;
          }

          &-file {
            width: 100%;
            height: 30px;
            margin-top: 10px;

            display: flex;
            justify-content: space-between;
            align-items: center;

            @media only screen and (max-width: 576px) {
              height: auto;

              flex-direction: column;
              align-items: flex-start;
              justify-content: flex-start;
            }

            &-title {
              padding: 8px 0 8px 40px;
              position: relative;

              color: #11bb2e;
              font-size: 14px;
              font-weight: 400;

              &:hover {
                cursor: pointer;
                text-decoration: underline;
              }

              &:before {
                width: 30px;
                height: 30px;
                margin: auto;

                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                content: '';

                background-image: url('../../../../assets/static/vacancies/folder.png');
                background-position: center;
                background-size: 14px 17px;
                background-repeat: no-repeat;

                background-color: #fff;
                border-radius: 50%;
              }
            }

            &-field {
              display: none;
            }

            &-text {
              width: 150px;

              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;

              @media only screen and (max-width: 576px) {
                width: 100%;
                margin-top: 10px;
              }
            }
          }

          &-btn {
            width: auto;
            margin-top: 10px;
            padding: 13px 107px 13px 135px;

            align-self: center;

            color: #fff;
            font-size: 12px;
            font-weight: 600;

            background-image: url('../../../../assets/static/vacancies/circle-correct.png');
            background-position: 105px center;
            background-size: 22px;
            background-repeat: no-repeat;

            background-color: #11bb2e;
            border: none;
            border-radius: 5px;
            outline: none;

            transition: .2s;

            @media only screen and (max-width: 576px) {
              width: 100%;
              padding: 13px 0;
              background-position: 10px center;
            }

            &:hover {
              cursor: pointer;
              background-color: rgba(17, 187, 46, 0.8);
            }
          }
        }
      }
      /* --- */
    }
    /* --- */
  }
</style>
